import { SalutationSubject, salutationFor } from "@signalist/shared-types";

import { TableColumn } from "@/components/Table/Table";

import { ListPerson } from "../../schema/list";
import { DEFAULT_COLUMN_OPTIONS_HIDE } from "./columnFactory";

/** The person the salutation is for: the prospect, the job offer's contact or the map business owner. */
type SubjectAccessor = (row: ListPerson) => SalutationSubject | null | undefined;

interface SalutationConfig {
	uid: string;
	titleKey: string;
	subjectAccessor: SubjectAccessor;
	lang: "de" | "en";
}

/**
 * A salutation column shows what the message variable will contain (PREC-4416): Herr/Frau
 * (Mr./Ms.) only for a gender confirmed at 90% or more, otherwise the first name.
 */
const createSalutationColumn = (
	ts: (key: string) => string,
	config: SalutationConfig
): TableColumn => {
	return {
		...DEFAULT_COLUMN_OPTIONS_HIDE,
		title: ts(config.titleKey),
		uid: config.uid,
		render: (row: ListPerson) => {
			const subject = config.subjectAccessor(row);

			return <div>{subject ? salutationFor(subject, config.lang) : ""}</div>;
		},
	};
};

// Salutation configurations
const salutationConfigs: Record<string, SalutationConfig> = {
	salutationDe: {
		uid: "salutationDe",
		titleKey: "salutationDe",
		subjectAccessor: (row) => row.prospect,
		lang: "de",
	},
	salutationEn: {
		uid: "salutationEn",
		titleKey: "salutationEn",
		subjectAccessor: (row) => row.prospect,
		lang: "en",
	},
	jobSalutationDe: {
		uid: "jobSalutationDe",
		titleKey: "salutationDe",
		subjectAccessor: (row) => row.offer,
		lang: "de",
	},
	jobSalutationEn: {
		uid: "jobSalutationEn",
		titleKey: "salutationEn",
		subjectAccessor: (row) => row.offer,
		lang: "en",
	},
	mapSalutationDe: {
		uid: "mapSalutationDe",
		titleKey: "salutationDe",
		subjectAccessor: (row) => row.mapBusiness,
		lang: "de",
	},
	mapSalutationEn: {
		uid: "mapSalutationEn",
		titleKey: "salutationEn",
		subjectAccessor: (row) => row.mapBusiness,
		lang: "en",
	},
};

// Export individual column functions for backwards compatibility
export const renderSalutationDeColumn = (ts: (key: string) => string): TableColumn =>
	createSalutationColumn(ts, salutationConfigs.salutationDe);

export const renderSalutationEnColumn = (ts: (key: string) => string): TableColumn =>
	createSalutationColumn(ts, salutationConfigs.salutationEn);

export const renderJobSalutationDeColumn = (ts: (key: string) => string): TableColumn =>
	createSalutationColumn(ts, salutationConfigs.jobSalutationDe);

export const renderJobSalutationEnColumn = (ts: (key: string) => string): TableColumn =>
	createSalutationColumn(ts, salutationConfigs.jobSalutationEn);

export const renderMapSalutationDeColumn = (ts: (key: string) => string): TableColumn =>
	createSalutationColumn(ts, salutationConfigs.mapSalutationDe);

export const renderMapSalutationEnColumn = (ts: (key: string) => string): TableColumn =>
	createSalutationColumn(ts, salutationConfigs.mapSalutationEn);
